import type { ReactNode } from 'react'

import { cn } from '../../lib/utils'

/**
 * The four tones, and the two shapes that carry them.
 *
 * The design's rule, from its own brief: **colour only where something is wrong.** So `neutral` is
 * the default and the three coloured tones each mean something specific. A chip that is green
 * because green is nice defeats the entire scheme — on a screen where nine domains are grey and
 * one is red, the red is the product.
 */
export type Tone = 'ok' | 'warn' | 'bad' | 'neutral' | 'accent' | 'info'

const CHIP: Record<Tone, string> = {
  ok: 'bg-okbg text-ok',
  warn: 'bg-warnbg text-warn',
  bad: 'bg-badbg text-bad',
  info: 'bg-infobg text-info',
  accent: 'bg-accent-soft text-accent',
  neutral: 'bg-sel text-muted',
}

const DOT: Record<Tone, string> = {
  ok: 'bg-ok',
  warn: 'bg-warn',
  bad: 'bg-bad',
  info: 'bg-info',
  accent: 'bg-accent',
  // Grey, and this one matters: "not checked" must never look like "checked and fine" (§49).
  neutral: 'bg-faint',
}

/**
 * A status pill. 11px, 20px radius, tone background — the design's shape.
 *
 * Plain case. The old system permitted uppercase here; this one does not use it anywhere except
 * the two places the design itself does, and neither is a status.
 */
export function Chip({
  tone = 'neutral',
  className,
  children,
}: {
  tone?: Tone
  className?: string
  children: ReactNode
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] whitespace-nowrap',
        CHIP[tone],
        className,
      )}
    >
      {children}
    </span>
  )
}

/**
 * A state as a single dot.
 *
 * Exists because a domain has four authentication checks and a mailbox has several: four chips per
 * row makes a scannable table unreadable, and four sentences fits three rows on a screen. The dot
 * is only legible next to a legend, so any screen using it carries one.
 *
 * The colour is the entire content, so it needs a text equivalent for anyone who cannot see it and
 * a tooltip for anyone who can but has not read the legend.
 */
export function Dot({
  tone = 'neutral',
  label,
  size = 7,
}: {
  tone?: Tone
  label: string
  size?: number
}) {
  return (
    <span
      role="img"
      aria-label={label}
      title={label}
      className={cn('inline-block shrink-0 rounded-full align-middle', DOT[tone])}
      style={{ width: size, height: size }}
    />
  )
}

/** A dot plus its meaning, for the legend that has to sit beside any table of dots. */
export function Legend({ tone, children }: { tone: Tone; children: ReactNode }) {
  return (
    <span className="text-faint inline-flex items-center gap-1.5 text-[11.5px]">
      <Dot tone={tone} label="" />
      {children}
    </span>
  )
}
